import { useNavigate } from '@tanstack/react-router';
import { Ban, Pause, Play, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '~/components/common/confirm-dialog';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';

import {
  useCancelDeliveryRun,
  usePauseDeliveryRun,
  useResumeDeliveryRun,
  useRetryFailedDeliveryRun,
} from '../hooks/use-delivery-controls';
import type { DeliveryRunDetail } from '../types/delivery-runs.types';
import { canRetryDelivery, deriveDeliveryActions } from '../utils/derive-delivery-actions';

import { RetryDeliveryButton } from './retry-delivery-button';

/**
 * Run control rail — pause / resume / cancel / retry-failed (driven by the run's
 * `availableActions`), plus a "retry delivery" that re-triggers the whole
 * delivery. Pause/resume are single click; the consequential cancel, retry, and
 * re-trigger are confirmed and start a NEW run. Renders nothing when no action
 * is available. `onRunCreated` lets a host outside the super-admin console
 * (e.g. the delivery wizard) follow the new run in place instead of navigating
 * to `/deliveries/$runId`.
 *
 * "Retry delivery" is `RetryDeliveryButton` (wizard in redeliver mode when the
 * run's catalog batch is known, else a one-click "redeliver everything"
 * confirm), shared with the products-unavailable card.
 */
export function DeliveryControlRail({
  run,
  onRunCreated,
  batchId,
}: {
  run: DeliveryRunDetail;
  onRunCreated?: (runId: string) => void;
  /** Catalog batch the run delivers, when the host knows it (wizard, Compare page). */
  batchId?: string | null | undefined;
}) {
  const { t } = useTranslation('deliveries');
  const navigate = useNavigate();
  // Default: open the new run on the console's run detail page.
  const openRun = (runId: string) => {
    if (onRunCreated) {
      onRunCreated(runId);
      return;
    }
    void navigate({ to: '/deliveries/$runId', params: { runId }, search: { tab: 'overview' } });
  };
  const actions = deriveDeliveryActions(run);

  const pause = usePauseDeliveryRun(run.id);
  const resume = useResumeDeliveryRun(run.id);
  const cancel = useCancelDeliveryRun(run.id);
  const retry = useRetryFailedDeliveryRun(run.id);
  const [confirm, setConfirm] = useState<'cancel' | 'retry' | null>(null);

  if (!actions.any && !canRetryDelivery(run)) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1 rounded-lg border p-1"
      data-testid="delivery-control-rail"
    >
      {actions.canPause ? (
        <Button
          variant="ghost"
          size="sm"
          data-testid="delivery-control-pause-button"
          isLoading={pause.isPending}
          onClick={() => {
            pause.mutate();
          }}
        >
          <Pause aria-hidden="true" />
          {t('controls.pause')}
        </Button>
      ) : null}

      {actions.canResume ? (
        <Button
          variant="ghost"
          size="sm"
          data-testid="delivery-control-resume-button"
          isLoading={resume.isPending}
          onClick={() => {
            resume.mutate();
          }}
        >
          <Play aria-hidden="true" />
          {t('controls.resume')}
        </Button>
      ) : null}

      {/* Below 1280px the retry actions collapse to icon buttons; the tooltip
          (only shown at that width) carries the label instead. */}
      {actions.canRetryFailed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              data-testid="delivery-control-retry-failed-button"
              isLoading={retry.isPending}
              variant="ghost"
              size="sm"
              className="cursor-pointer"
              aria-label={t('controls.retry')}
              onClick={() => {
                setConfirm('retry');
              }}
            >
              <RotateCcw aria-hidden="true" />
              <span className="hidden xl:inline">{t('controls.retry')}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="xl:hidden">{t('controls.retry')}</TooltipContent>
        </Tooltip>
      ) : null}

      <RetryDeliveryButton run={run} batchId={batchId} onRunCreated={onRunCreated} />

      {actions.canCancel ? (
        <>
          <span className="bg-border mx-0.5 h-5 w-px" aria-hidden="true" />
          <Button
            data-testid="delivery-control-cancel-button"
            isLoading={cancel.isPending}
            variant="ghost"
            size="sm"
            className="text-error hover:text-error"
            onClick={() => {
              setConfirm('cancel');
            }}
          >
            <Ban aria-hidden="true" />
            {t('controls.cancel')}
          </Button>
        </>
      ) : null}

      <ConfirmDialog
        open={confirm === 'cancel'}
        testId="delivery-control-cancel-dialog"
        onOpenChange={(o) => {
          if (!o) setConfirm(null);
        }}
        title={t('controls.confirm.cancelTitle')}
        description={t('controls.confirm.cancelBody')}
        confirmLabel={t('controls.confirm.cancelConfirm')}
        cancelLabel={t('controls.confirm.dismiss')}
        isPending={cancel.isPending}
        onConfirm={() => {
          cancel.mutate(undefined, {
            onSuccess: () => {
              setConfirm(null);
            },
          });
        }}
      />

      <ConfirmDialog
        open={confirm === 'retry'}
        testId="delivery-control-retry-failed-dialog"
        onOpenChange={(o) => {
          if (!o) setConfirm(null);
        }}
        title={t('controls.confirm.retryTitle')}
        description={t('controls.confirm.retryBody')}
        confirmLabel={t('controls.confirm.retryConfirm')}
        cancelLabel={t('controls.confirm.dismiss')}
        isPending={retry.isPending}
        onConfirm={() => {
          retry.mutate(undefined, {
            onSuccess: (data) => {
              setConfirm(null);
              openRun(data.newRunId);
            },
          });
        }}
      />
    </div>
  );
}
